import { useState, useRef, useEffect } from 'react';
import { Download, FileText, File, FileDown } from 'lucide-react';
import { exportFile } from '../services/fileExport';

interface DownloadMenuProps {
  content: string;
  fileName: string;
  compact?: boolean;
}

export default function DownloadMenu({ content, fileName, compact = false }: DownloadMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownload = async (format: 'pdf' | 'docx' | 'txt') => {
    try {
      setIsExporting(true);
      await exportFile(content, fileName, format);
      setIsOpen(false);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erro ao fazer download do arquivo. Por favor, tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isExporting}
        className={`flex items-center transition-all duration-200 ${
          compact
            ? 'p-2 rounded-lg text-accent-500 hover:text-accent-600 hover:bg-accent-50 dark:text-accent-400 dark:hover:text-accent-300 dark:hover:bg-accent-900/20'
            : 'px-4 py-2 rounded-lg btn-accent'
        }`}
        title="Download"
      >
        <Download className="h-5 w-5" />
        {!compact && <span className="ml-2">Download</span>}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 rounded-xl shadow-xl bg-white dark:bg-navy-800 border border-navy-200 dark:border-navy-700 z-50 overflow-hidden">
          <div className="py-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload('pdf');
              }}
              disabled={isExporting}
              className="flex items-center w-full px-4 py-3 text-sm text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors duration-200"
            >
              <FileText className="h-4 w-4 mr-3 text-error-500" />
              PDF
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload('docx');
              }}
              disabled={isExporting}
              className="flex items-center w-full px-4 py-3 text-sm text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors duration-200"
            >
              <File className="h-4 w-4 mr-3 text-primary-500 dark:text-primary-400" />
              Word Document
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload('txt');
              }}
              disabled={isExporting}
              className="flex items-center w-full px-4 py-3 text-sm text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors duration-200"
            >
              <FileDown className="h-4 w-4 mr-3 text-navy-500" />
              Texto (.txt)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
